import { useState } from "react";
import { Link, Navigate, useNavigate } from "@tanstack/react-router";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { orgsQuery, type ChooserStore } from "../lib/store";
import { TopBar } from "../components/Shell";
import { Badge, Button, Card, EmptyState, ErrorState, Input, Skeleton } from "../components/ui";

const PAGE = 30;

export function Chooser() {
  const { t, relative } = useI18n();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const orgs = useQuery(orgsQuery);
  const stores = useInfiniteQuery({
    queryKey: ["chooser", q],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      api<{ stores: ChooserStore[]; has_more: boolean }>(`/admin/v1/me/stores?limit=${PAGE}&offset=${pageParam}&q=${encodeURIComponent(q)}`),
    getNextPageParam: (last, all) => (last.has_more ? all.length * PAGE : undefined),
  });

  const list = stores.data?.pages.flatMap((p) => p.stores) ?? [];
  // New users (no organization yet) go through onboarding first. Invited
  // staff belong to stores without an organization of their own: they skip it.
  if (orgs.data && orgs.data.length === 0 && stores.data && !q && list.length === 0) return <Navigate to="/onboarding" />;
  const partnerOrgs = orgs.data?.filter((o) => o.type === "partner") ?? [];

  return (
    <div className="min-h-full">
      <TopBar />
      <div className="mx-auto max-w-2xl space-y-4 px-4 py-8">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-xl font-semibold">{t("chooser.title")}</h1>
          <Link to="/stores/new"><Button variant="primary">{t("chooser.create")}</Button></Link>
        </div>
        {partnerOrgs.map((o) => (
          <Link key={o.id} to="/partners/$org" params={{ org: o.id }} className="block">
            <Card><div className="flex items-center justify-between"><span className="font-medium">{o.name}</span><span className="text-sm text-stone-600">{t("chooser.partner_dashboard")} →</span></div></Card>
          </Link>
        ))}
        <Input type="search" placeholder={t("chooser.search")} value={q} onChange={(e) => setQ(e.target.value)} aria-label={t("chooser.search")} />
        <Card>
          {stores.isPending ? <Skeleton /> : stores.isError ? <ErrorState error={stores.error} retry={() => stores.refetch()} /> :
            list.length === 0 && !q ? (
              <EmptyState title={t("chooser.empty_title")} body={t("chooser.empty_body")}
                action={<Button variant="primary" onClick={() => navigate({ to: "/stores/new" })}>{t("chooser.create")}</Button>} />
            ) : list.length === 0 ? <p className="text-sm text-stone-500">{t("common.none")}</p> : (
              <ul className="-m-2 divide-y divide-stone-100">
                {list.map((s) => (
                  <li key={s.id}>
                    <Link to="/store/$handle" params={{ handle: s.handle }} className="flex items-center gap-3 rounded-lg p-3 hover:bg-stone-50">
                      <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-stone-800 font-semibold text-white">{s.name.slice(0, 1).toUpperCase()}</div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium">{s.name}</div>
                        <div className="truncate text-xs text-stone-500" dir="ltr">{s.handle}.{import.meta.env.VITE_STOREFRONT_DOMAIN}{s.org_name ? ` · ${s.org_name}` : ""}</div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <div className="flex gap-1">
                          {s.is_development && <Badge tone="info">{t("chooser.development")}</Badge>}
                          <Badge>{t(`chooser.role.${s.role}`)}</Badge>
                        </div>
                        {s.last_opened_at && <span className="text-xs text-stone-500">{t("chooser.last_opened", { when: relative(s.last_opened_at) })}</span>}
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          {stores.hasNextPage && <div className="mt-3 text-center"><Button onClick={() => stores.fetchNextPage()} loading={stores.isFetchingNextPage}>{t("chooser.load_more")}</Button></div>}
        </Card>
      </div>
    </div>
  );
}
